/**
 * Content skeleton for the finance app. Renders the shape of a typical page
 * (header row · hero card · KPI tiles · two widgets) with shimmering
 * placeholders, so a loading page reads as the app filling in widget by
 * widget rather than a bare full-screen spinner. Used by the route-level
 * loading.tsx and by FinanceChrome while the data provider warms up.
 */

function Block({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded-lg bg-surface-3 ${className}`} />;
}

function CardShell({ children }: { children: React.ReactNode }) {
  return <div className="rounded-2xl border border-edge bg-surface-1 p-4 sm:p-5">{children}</div>;
}

export function FinanceSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading">
      {/* Page header: title + range picker */}
      <div className="flex items-center justify-between gap-3">
        <Block className="h-7 w-32" />
        <Block className="h-8 w-52 rounded-full" />
      </div>

      {/* Hero card */}
      <CardShell>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="space-y-2.5">
            <Block className="h-3 w-24" />
            <Block className="h-11 w-52" />
            <Block className="h-3 w-32" />
          </div>
          <Block className="h-40 min-w-0 flex-1 basis-72 rounded-xl" />
        </div>
      </CardShell>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-2 rounded-2xl border border-edge bg-surface-1 p-4">
            <Block className="h-3 w-16" />
            <Block className="h-6 w-24" />
          </div>
        ))}
      </div>

      {/* Two widgets */}
      <div className="grid gap-4 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <CardShell key={i}>
            <Block className="mb-3 h-4 w-28" />
            <Block className="h-48 w-full rounded-xl" />
          </CardShell>
        ))}
      </div>
    </div>
  );
}
